<template>
  <div class="dashboard-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="openEmailContactsModal" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <div class="topbar">
        <div class="topbar-right">
          <span class="welcome-text">Welcome back!</span>
          <button class="btn-logout" @click="logout">Logout</button>
        </div>
      </div>
      <!-- CONTENT -->
      <div class="content">
        <!-- Loading State -->
        <div v-if="loadingGlobalStats" class="loading-state">
          <div class="spinner"></div>
          <p>Loading dashboard...</p>
        </div>

        <!-- Dashboard Content -->
        <div v-else>
          <!-- STAT CARDS -->
          <div class="stats-row">
            <div class="stat-card">
              <div class="info">
                <div class="label">Total Properties</div>
                <div class="value">{{ globalStats.totalProperties || 0 }}</div>
                <div class="sub">Under management</div>
              </div>
              <div class="stat-icon purple">🏠</div>
            </div>

            <div class="stat-card">
              <div class="info">
                <div class="label">Reports This Month</div>
                <div class="value">{{ reportsStats.sent }} <span style="font-size:1.1rem;color:var(--text-muted);font-weight:500;">/ {{ reportsStats.total }}</span></div>
                <div class="sub">{{ reportsStats.toSend }} still to send</div>
              </div>
              <div class="stat-icon amber">📋</div>
            </div>

            <div class="stat-card">
              <div class="info">
                <div class="label">Total Bookings</div>
                <div class="value">{{ globalStats.totalBookings || 0 }}</div>
                <div class="sub">This month, all properties</div>
              </div>
              <div class="stat-icon green">📅</div>
            </div>
          </div>

          <!-- COMMISSION BANNER -->
          <div class="commission-banner">
            <div class="commission-main">
              <div class="commission-label">Your Commission This Month — All Properties</div>
              <div class="commission-value">{{ formatCurrency(globalStats.totalManagementCommission || 0) }}</div>
              <div class="commission-sub">{{ getCurrentMonthYear() }} · {{ globalStats.totalProperties || 0 }} properties · inc. VAT · updates in real time</div>
            </div>
            <div class="commission-breakdown">
              <div class="breakdown-item">
                <div class="b-label">Ex. VAT</div>
                <div class="b-value">{{ formatCurrency(globalStats.totalManagementCommissionExVAT || 0) }}</div>
              </div>
              <div class="breakdown-item">
                <div class="b-label">VAT</div>
                <div class="b-value">{{ formatCurrency(globalStats.totalVAT || 0) }}</div>
              </div>
              <div class="breakdown-item">
                <div class="b-label">Owner Settlements</div>
                <div class="b-value">
                  {{ formatCurrency(globalStats.totalOwnerNetEarnings || 0) }}
                </div>
              </div>
            </div>
          </div>

          <!-- BOTTOM GRID -->
          <div class="bottom-grid">
            <!-- REPORTS CARD -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title">Month-end Reports</div>
                  <div style="font-size:0.72rem;color:var(--text-muted);margin-top:2px;">{{ getCurrentMonthYear() }}</div>
                </div>
                <div style="display:flex;align-items:center;gap:10px;">
                  <div class="month-tabs">
                    <div class="month-tab active">{{ currentMonthShort }}</div>
                    <div class="month-tab">{{ previousMonthShort }}</div>
                  </div>
                  <router-link to="/finance/month-end" class="card-action">Go to Month-end →</router-link>
                </div>
              </div>
              <div class="card-body">
                <!-- progress summary -->
                <div class="reports-summary">
                  <div class="progress-ring-wrap">
                    <svg width="72" height="72" viewBox="0 0 72 72">
                      <circle cx="36" cy="36" r="28" fill="none" stroke="#ebe9f8" stroke-width="7"/>
                      <circle cx="36" cy="36" r="28" fill="none" stroke="var(--purple)" stroke-width="7"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="progressOffset"
                        stroke-linecap="round"/>
                    </svg>
                    <div class="ring-center">
                      <div class="pct">{{ completionPercentage }}%</div>
                      <div class="pct-lbl">sent</div>
                    </div>
                  </div>
                  <div class="reports-counts">
                    <div class="count-row">
                      <span class="count-lbl"><span class="count-dot" style="background:#f59e0b;"></span>To send</span>
                      <span class="count-val">{{ reportsStats.toSend }}</span>
                    </div>
                    <div class="count-row">
                      <span class="count-lbl"><span class="count-dot" style="background:#00b894;"></span>Sent</span>
                      <span class="count-val">{{ reportsStats.sent }}</span>
                    </div>
                    <div class="count-row">
                      <span class="count-lbl"><span class="count-dot" style="background:#ebe9f8;"></span>Total</span>
                      <span class="count-val">{{ reportsStats.total }}</span>
                    </div>
                  </div>
                </div>

                <!-- report rows -->
                <div class="report-rows">
                  <div
                    v-for="property in topPropertiesByCommission.slice(0, 4)"
                    :key="property.id"
                    class="report-row"
                  >
                    <div>
                      <div class="prop">{{ property.name }}</div>
                      <div class="owner">Owner: {{ getPropertyOwner(property) }}</div>
                    </div>
                    <div class="fee">{{ formatCurrency(property.managementCommission || 0) }}</div>
                    <span v-if="property.reportStatus === 'sent'" class="status-pill sent">Sent ✓</span>
                    <span v-else-if="property.reportStatus === 'pushed_to_xero'" class="status-pill pushed">Xero ✓</span>
                    <span v-else-if="property.reportStatus === 'ready_to_send'" class="status-pill ready">Ready</span>
                    <span v-else-if="property.reportStatus === 'draft'" class="status-pill draft">Draft</span>
                    <span v-else class="status-pill pending">Pending</span>
                  </div>
                  <div v-if="topPropertiesByCommission.length === 0" class="empty-state-small">
                    <p>No reports available</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- PROPERTIES CARD -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title">Commission by Property</div>
                  <div style="font-size:0.72rem;color:var(--text-muted);margin-top:2px;">{{ getCurrentMonthYear() }} · ranked by commission</div>
                </div>
                <div class="month-tabs">
                  <div class="month-tab active">{{ currentMonthShort }}</div>
                  <div class="month-tab">{{ previousMonthShort }}</div>
                </div>
              </div>
              <div class="card-body">
                <div class="prop-list">
                  <div
                    v-for="property in topPropertiesByCommission.slice(0, 6)"
                    :key="property.id"
                    class="prop-row"
                  >
                    <div class="prop-initial">{{ getPropertyInitials(property.name) }}</div>
                    <div class="prop-info">
                      <div class="prop-name">{{ property.name }}</div>
                      <div class="prop-owner">{{ getPropertyOwner(property) }}</div>
                    </div>
                    <div style="text-align:right;">
                      <div class="prop-commission">{{ formatCurrency(property.managementCommission || 0) }}</div>
                      <div class="prop-commission-lbl">commission</div>
                    </div>
                  </div>
                  <div v-if="topPropertiesByCommission.length === 0" class="empty-state-small">
                    <p>No commission data available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'DashboardView',
  components: {
    Sidebar
  },
  data() {
    return {
      isCollapsed: false,
      baseUrl: SETTINGS.API.baseUrl,

      // Properties data
      properties: [],

      // Global statistics (aggregated from all properties)
      globalStats: {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0,
        totalBookings: 0,
        totalManagementCommission: 0, // Real management fees
        totalManagementCommissionExVAT: 0, // Management fees without VAT
        totalVAT: 0, // VAT on management fees
        totalOwnerNetEarnings: 0
      },

      // Real reports statistics from backend MonthlyReport model
      monthEndReportsStats: {
        total: 0,
        sent: 0,
        ready_to_send: 0,
        pushed_to_xero: 0
      },

      // Month-end reports with their real statuses
      monthEndReports: [], // List of reports from backend with status for each property

      // Individual property data for calculations
      propertiesData: [],

      // Global reservations lists (all properties combined)
      globalArrivingToday: [],
      globalActiveReservations: [],

      // Selected property for reservations
      selectedPropertyForReservations: null,
      selectedPropertyData: {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      },

      // Loading states
      loadingGlobalStats: true,
      loadingPropertyReservations: true,

      // UI states
      showReservations: false
    };
  },

  computed: {
    // Month tabs
    currentMonthShort() {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[new Date().getMonth()];
    },
    previousMonthShort() {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const prevMonth = new Date().getMonth() - 1;
      return months[prevMonth < 0 ? 11 : prevMonth];
    },

    // Reports statistics - from backend MonthlyReport model
    reportsStats() {
      return {
        total: this.monthEndReportsStats.total || 0,
        sent: (this.monthEndReportsStats.sent || 0) + (this.monthEndReportsStats.pushed_to_xero || 0),
        toSend: this.monthEndReportsStats.ready_to_send || 0
      };
    },

    // Progress ring calculations
    circumference() {
      const radius = 28;
      return 2 * Math.PI * radius;
    },
    completionPercentage() {
      const stats = this.reportsStats;
      if (!stats.total || stats.total === 0) return 0;
      return Math.round((stats.sent / stats.total) * 100);
    },
    progressOffset() {
      const percentage = this.completionPercentage;
      return this.circumference - (percentage / 100) * this.circumference;
    },

    // Top properties by commission
    topPropertiesByCommission() {
      if (!this.propertiesData || this.propertiesData.length === 0) return [];

      return [...this.propertiesData]
        .sort((a, b) => (b.managementCommission || 0) - (a.managementCommission || 0))
        .slice(0, 6);
    },

    maxCommission() {
      if (this.topPropertiesByCommission.length === 0) return 0;
      return this.topPropertiesByCommission[0].managementCommission || 0;
    }
  },

  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },

    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },

    openEmailContactsModal() {
      this.$router.push('/reservations?openContacts=true');
    },

    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },

    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },

    getPropertyPercentage(commission) {
      if (!this.maxCommission || !commission) return 0;
      return Math.round((commission / this.maxCommission) * 100);
    },

    getCurrentDate() {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    },

    getCurrentMonthYear() {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${months[now.getMonth()]} ${now.getFullYear()}`;
    },

    getPropertyInitials(name) {
      if (!name) return '??';
      const words = name.trim().split(' ').filter(w => w.length > 0);
      if (words.length === 0) return '??';
      if (words.length === 1) {
        // Un seul mot : prendre les 2 premières lettres
        return words[0].substring(0, 2).toUpperCase();
      }
      // Plusieurs mots : première lettre de chaque mot
      return (words[0][0] + words[1][0]).toUpperCase();
    },

    getPropertyOwner(property) {
      return 'Owner'; // Placeholder - replace with actual owner data if available
    },

    async fetchMonthEndReportsStats() {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Get current year and month
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1; // JS months are 0-indexed

        const response = await axios.get(`${this.baseUrl}/api/pms/month-end/`, {
          headers,
          params: {
            year: year,
            month: month,
            status: 'all'
          }
        });

        if (response.data.success && response.data.data) {
          this.monthEndReportsStats = response.data.data.stats || {
            total: 0,
            sent: 0,
            ready_to_send: 0,
            pushed_to_xero: 0
          };

          // Store the complete reports list with statuses
          this.monthEndReports = response.data.data.reports || [];
        }
      } catch (error) {
        console.error('Error fetching month-end reports stats:', error);
        // Keep default values (all zeros) if API fails
        this.monthEndReportsStats = {
          total: 0,
          sent: 0,
          ready_to_send: 0,
          pushed_to_xero: 0
        };
        this.monthEndReports = [];
      }
    },

    async fetchDashboardData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      // Get selected PMS configuration from localStorage
      const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
      if (!selectedPms || !selectedPms.id) {
        alert('No PMS configuration selected. Please select a PMS from the Hub.');
        this.$router.push('/');
        return;
      }

      const pmsConfigId = selectedPms.id;
      const headers = { 'Authorization': `Bearer ${token}` };
      this.loadingGlobalStats = true;

      // Fetch month-end reports stats in parallel with properties
      this.fetchMonthEndReportsStats();

      try {
        // Step 1: Fetch all properties using Django endpoint
        const propertiesRes = await axios.get(`${this.baseUrl}/api/pms/properties/`, {
          headers,
          params: { pms_config_id: pmsConfigId }
        });
        this.properties = propertiesRes.data.properties.data;

        // Set default selected property for reservations (first property)
        if (this.properties.length > 0 && !this.selectedPropertyForReservations) {
          this.selectedPropertyForReservations = this.properties[0].id;
        }

        // Properties are loaded

        // Step 2: Fetch data for each property separately with batching to avoid rate limiting
        this.propertiesData = [];
        const batchSize = 5; // Process 5 properties at a time
        const delayBetweenBatches = 1000; // 1 second delay between batches

        for (let i = 0; i < this.properties.length; i += batchSize) {
          const batch = this.properties.slice(i, i + batchSize);

          const batchPromises = batch.map(async (property) => {
            try {
              // Add small delay between individual requests within batch
              await new Promise(resolve => setTimeout(resolve, 200 * (i % batchSize)));

              const [arrivingTodayRes, activeTodayRes, monthRevenueRes, settingsRes] = await Promise.all([
                axios.get(`${this.baseUrl}/api/pms/reservations/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, filter: 'booked', property_id: property.id }
                }),
                axios.get(`${this.baseUrl}/api/pms/reservations/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, filter: 'today', property_id: property.id }
                }),
                axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, property_id: property.id }
                }),
                axios.get(`${this.baseUrl}/api/pms/settings/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, property_id: property.id }
                })
              ]);

              // Parse Django API responses
              const arrivingToday = arrivingTodayRes.data.reservations?.data || [];
              const activeToday = activeTodayRes.data.reservations?.data || [];
              const monthReservations = monthRevenueRes.data.reservations || [];
              const settings = settingsRes.data.data || {
                management_fee_rate: 16,
                management_fee_base: 'net',
                management_fee_fixed_amount: 500,
                vat_rate: 20,
                vat_business_model: 'agent',
                maintenance: 0,
                consumables: 20,
                other_expenses: 0,
                laundry_fee: 0,
                laundry_per_booking: 15,
                laundry_calculation_mode: 'fixed',
                cleaning_mode: 'rentlio'
              };

              // Convert to array if it's an object
              const monthReservationsArray = Array.isArray(monthReservations)
                ? monthReservations
                : Object.values(monthReservations);

              // Calculate month statistics
              const monthRevenue = monthReservationsArray.reduce((total, res) =>
                total + (res.total_reservation_price || res.totalReservationPrice || 0), 0);

              const monthBookings = monthReservationsArray.length;

              // Calculate REAL financial metrics based on settings
              let totalRevenue = 0;
              let totalPlatformFees = 0;
              let totalCardFees = 0;
              let totalOutgoings = 0;

              monthReservationsArray.forEach(res => {
                totalRevenue += parseFloat(res.total_reservation_price || res.totalReservationPrice || 0);
                totalPlatformFees += parseFloat(res.channel_commission_nominal || res.channelCommissionNominal || 0);
                totalCardFees += parseFloat(res.card_fee || res.cardFee || 0);
                totalOutgoings += parseFloat(res.total_services_price || res.totalServicesPrice || 0);
              });

              const netIncome = totalRevenue - totalPlatformFees - totalCardFees;
              const grossProfit = netIncome - totalOutgoings;

              // Calculate REAL Management Fees based on settings
              let managementFees = 0;
              const feeBase = settings.management_fee_base || 'net';
              const feeRate = parseFloat(settings.management_fee_rate || 16);

              if (feeBase === 'revenue') {
                managementFees = totalRevenue * (feeRate / 100);
              } else if (feeBase === 'gross') {
                managementFees = grossProfit * (feeRate / 100);
              } else if (feeBase === 'grossFixed') {
                managementFees = parseFloat(settings.management_fee_fixed_amount || 500);
              } else { // 'net'
                managementFees = netIncome * (feeRate / 100);
              }

              // Calculate VAT on management fees and supplied services
              const vatRate = parseFloat(settings.vat_rate || 20) / 100;
              const consumables = parseFloat(settings.consumables || 0);
              const laundryFee = settings.laundry_calculation_mode === 'per_booking'
                ? monthBookings * parseFloat(settings.laundry_per_booking || 15)
                : parseFloat(settings.laundry_fee || 0);

              // VAT is applied on management fees + consumables + laundry (agent model)
              const taxableAmount = managementFees + consumables + laundryFee;
              const vatAmount = taxableAmount * vatRate;
              const managementFeesIncVAT = managementFees + vatAmount;

              // Calculate owner net earnings (settlement amount)
              const maintenance = parseFloat(settings.maintenance || 0);
              const otherExpenses = parseFloat(settings.other_expenses || 0);
              const settlementAmount = grossProfit - managementFees - vatAmount - consumables - laundryFee - maintenance - otherExpenses;

              // Find the monthly report status for this property
              const report = this.monthEndReports.find(r => r.property_id === property.id);
              const reportStatus = report ? report.status : null;

              return {
                id: property.id,
                name: property.name,
                arrivingToday: arrivingToday,
                arrivingTodayCount: arrivingToday.length,
                arrivingTodayCost: arrivingToday.reduce((total, res) => total + (res.total_reservation_price || res.totalReservationPrice || 0), 0),
                activeReservations: activeToday,
                activeReservationsCount: activeToday.length,
                activeReservationsCost: activeToday.reduce((total, res) => total + (res.total_reservation_price || res.totalReservationPrice || 0), 0),
                monthRevenue: monthRevenue,
                monthBookings: monthBookings,
                managementCommission: managementFeesIncVAT, // Real management fees (inc VAT)
                managementCommissionExVAT: managementFees, // Real management fees (ex VAT)
                vatAmount: vatAmount, // VAT on management fees
                ownerNetEarnings: settlementAmount, // Real owner settlement
                reportStatus: reportStatus // Real report status from MonthlyReport model
              };
            } catch (error) {
              console.error(`Error fetching data for property ${property.name}:`, error);
              // Find the monthly report status for this property even on error
              const report = this.monthEndReports.find(r => r.property_id === property.id);
              const reportStatus = report ? report.status : null;

              return {
                id: property.id,
                name: property.name,
                arrivingToday: [],
                arrivingTodayCount: 0,
                arrivingTodayCost: 0,
                activeReservations: [],
                activeReservationsCount: 0,
                activeReservationsCost: 0,
                monthRevenue: 0,
                monthBookings: 0,
                managementCommission: 0,
                managementCommissionExVAT: 0,
                vatAmount: 0,
                ownerNetEarnings: 0,
                reportStatus: reportStatus
              };
            }
          });

          // Process current batch
          const batchResults = await Promise.all(batchPromises);
          this.propertiesData.push(...batchResults);

          // Update global stats after each batch for progressive loading
          this.calculateGlobalStats();

          // After first batch, property reservations section can be shown
          if (i === 0) {
            this.loadingPropertyReservations = false;
            // Load selected property reservations
            this.fetchSelectedPropertyReservations();
          }

          // Add delay before next batch (except for last batch)
          if (i + batchSize < this.properties.length) {
            await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
          }
        }


        // Final step: Show global stats

        // All data loaded, show global statistics
        this.loadingGlobalStats = false;

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } finally {
        // Ensure all loading states are disabled even if there's an error
        this.loadingGlobalStats = false;
        this.loadingPropertyReservations = false;
      }
    },

    calculateGlobalStats() {

      const totalArrivingToday = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCount || 0), 0) || 0;
      const totalArrivingTodayCost = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCost || 0), 0) || 0;
      const totalActiveReservations = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCount || 0), 0) || 0;
      const totalActiveReservationsCost = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCost || 0), 0) || 0;
      const totalMonthRevenue = this.propertiesData?.reduce((total, prop) => total + (prop.monthRevenue || 0), 0) || 0;
      const totalBookings = this.propertiesData?.reduce((total, prop) => total + (prop.monthBookings || 0), 0) || 0;
      const totalManagementCommission = this.propertiesData?.reduce((total, prop) => total + (prop.managementCommission || 0), 0) || 0;
      const totalManagementCommissionExVAT = this.propertiesData?.reduce((total, prop) => total + (prop.managementCommissionExVAT || 0), 0) || 0;
      const totalVAT = this.propertiesData?.reduce((total, prop) => total + (prop.vatAmount || 0), 0) || 0;
      const totalOwnerNetEarnings = this.propertiesData?.reduce((total, prop) => total + (prop.ownerNetEarnings || 0), 0) || 0;

      // Build global reservations lists (all properties combined)
      this.globalArrivingToday = [];
      this.globalActiveReservations = [];

      this.propertiesData?.forEach(prop => {
        // Add property name to each reservation for display
        if (prop.arrivingToday && Array.isArray(prop.arrivingToday)) {
          prop.arrivingToday.forEach(res => {
            this.globalArrivingToday.push({
              ...res,
              propertyName: prop.name
            });
          });
        }

        if (prop.activeReservations && Array.isArray(prop.activeReservations)) {
          prop.activeReservations.forEach(res => {
            this.globalActiveReservations.push({
              ...res,
              propertyName: prop.name
            });
          });
        }
      });

      // Ensure globalStats always has proper structure with default values
      this.globalStats = {
        totalProperties: this.properties?.length || 0,
        totalArrivingToday: totalArrivingToday,
        totalArrivingTodayCost: totalArrivingTodayCost,
        totalActiveReservations: totalActiveReservations,
        totalActiveReservationsCost: totalActiveReservationsCost,
        totalMonthRevenue: totalMonthRevenue,
        totalBookings: totalBookings,
        totalManagementCommission: totalManagementCommission,
        totalManagementCommissionExVAT: totalManagementCommissionExVAT,
        totalVAT: totalVAT,
        totalOwnerNetEarnings: totalOwnerNetEarnings
      };

    },

    async fetchSelectedPropertyReservations() {
      if (!this.selectedPropertyForReservations) return;

      const propertyData = this.propertiesData.find(p => p.id == this.selectedPropertyForReservations);
      if (propertyData) {
        this.selectedPropertyData = {
          arrivingToday: propertyData.arrivingToday,
          arrivingTodayCount: propertyData.arrivingTodayCount,
          activeReservations: propertyData.activeReservations,
          activeReservationsCount: propertyData.activeReservationsCount
        };
      }
    },

    initializeDefaults() {
      // Initialize all data structures to prevent undefined errors
      this.globalStats = {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0
      };

      this.selectedPropertyData = {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      };

      // Initialize loading states
      this.loadingGlobalStats = true;
      this.loadingPropertyReservations = true;
    }
  },

  created() {
    // Ensure globalStats is properly initialized even before API calls
    this.initializeDefaults();
  },

  mounted() {
    this.fetchDashboardData();
  }
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --sidebar:     #5b58d4;
  --sidebar-lt:  #6b4fd8;
  --purple:      #8075f0;
  --purple-lt:   #a99ff6;
  --purple-dim:  rgba(128,117,240,0.1);
  --purple-border: rgba(128,117,240,0.22);
  --navy:        #0f0e2e;
  --bg:          #f5f3ff;
  --white:       #ffffff;
  --text:        #12113a;
  --text-mid:    #4a4870;
  --text-muted:  #9896b8;
  --border:      #ebe9f8;
  --green:       #00b894;
  --green-dim:   rgba(0,184,148,0.1);
  --amber:       #f59e0b;
  --amber-dim:   rgba(245,158,11,0.1);
  --gold:        #c8a84b;
}

* {
  font-family: 'DM Sans', sans-serif;
}

h1, h2, h3, .value, .commission-value, .pct, .count-val, .prop-commission {
  font-family: 'Bricolage Grotesque', sans-serif;
}

.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f5f3ff !important;
}

/* ── SIDEBAR ── */
/* Sidebar utilise les styles du composant Sidebar.vue */

/* ── MAIN ── */
.main {
  margin-left: 218px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── TOPBAR ── */
.topbar {
  background: white;
  border-bottom: 1px solid #ebe9f8;
  padding: 0 28px;
  height: 60px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 50;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  font-family: 'Bricolage Grotesque', sans-serif;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.welcome-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.btn-logout {
  padding: 8px 16px;
  background: #ef4444;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

.content {
  padding: 28px 36px;
  flex: 1;
}

/* ── LOADING STATE ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  gap: 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #5b58d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-state p {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ── STAT CARDS ── */
.stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  animation: fadeIn 0.4s 0.05s both;
}

.stat-card {
  background: #ffffff !important;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  transition: box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.stat-card .info {
  flex: 1;
}

.stat-card .label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.stat-card .value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.9rem;
  font-weight: 800;
  color: var(--text);
  line-height: 1;
}

.stat-card .sub {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 5px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.stat-icon.purple {
  background: var(--purple-dim);
}

.stat-icon.green {
  background: var(--green-dim);
}

.stat-icon.amber {
  background: var(--amber-dim);
}

/* ── COMMISSION BANNER ── */
.commission-banner {
  background: linear-gradient(135deg, #3430a0 0%, #4a46b8 100%);
  border-radius: 14px;
  padding: 24px 28px;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.4s 0.12s both;
}

.commission-banner::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
  pointer-events: none;
}

.commission-banner::after {
  content: '';
  position: absolute;
  bottom: -40px;
  left: 30%;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
  pointer-events: none;
}

.commission-main {
  position: relative;
  z-index: 1;
}

.commission-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.5);
  margin-bottom: 6px;
}

.commission-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 2.6rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.commission-sub {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  margin-top: 6px;
}

.commission-breakdown {
  display: flex;
  gap: 28px;
  position: relative;
  z-index: 1;
}

.breakdown-item {
  text-align: right;
}

.breakdown-item .b-label {
  font-size: 0.62rem;
  color: rgba(255,255,255,0.4);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.breakdown-item .b-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
}

.calculate-btn-inline {
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'DM Sans', sans-serif;
}

.calculate-btn-inline:hover {
  background: rgba(255,255,255,0.3);
}

.mini-spinner {
  border: 2px solid rgba(255,255,255,0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

/* ── BOTTOM GRID ── */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  animation: fadeIn 0.4s 0.2s both;
}

/* ── CARDS ── */
.card {
  background: #ffffff !important;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);
}

.card-action {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--purple);
  text-decoration: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid var(--purple-border);
  background: #f7f6ff;
  transition: background 0.15s;
}

.card-action:hover {
  background: var(--purple-dim);
}

.card-body {
  padding: 18px 20px;
}

/* progress ring */
.reports-summary {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 18px;
}

.progress-ring-wrap {
  position: relative;
  flex-shrink: 0;
}

.progress-ring-wrap svg {
  transform: rotate(-90deg);
}

.ring-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.pct {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--text);
}

.pct-lbl {
  font-size: 0.55rem;
  color: var(--text-muted);
}

.reports-counts {
  flex: 1;
}

.count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 0.82rem;
  border-bottom: 1px solid var(--border);
}

.count-row:last-child {
  border: none;
}

.count-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  display: inline-block;
}

.count-lbl {
  color: var(--text-mid);
  display: flex;
  align-items: center;
}

.count-val {
  font-weight: 700;
  color: var(--text);
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.95rem;
}

/* report rows */
.report-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.report-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
  gap: 12px;
}

.report-row:last-child {
  border: none;
}

.report-row .prop {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
}

.report-row .owner {
  font-size: 0.72rem;
  color: var(--text-muted);
}

.report-row .fee {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  min-width: 60px;
  text-align: right;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 0.62rem;
  font-weight: 700;
}

.status-pill.pending {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}

.status-pill.draft {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.status-pill.ready {
  background: #fef3c7;
  color: #f59e0b;
  border: 1px solid #fcd34d;
}

.status-pill.sent {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-pill.pushed {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #93c5fd;
}

/* ── PROPERTIES CARD ── */
.prop-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.prop-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid var(--border);
}

.prop-row:last-child {
  border: none;
}

.prop-initial {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f2f1fd;
  border: 1px solid var(--purple-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--purple);
  flex-shrink: 0;
}

.prop-info {
  flex: 1;
}

.prop-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
}

.prop-owner {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.prop-commission {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
}

.prop-commission-lbl {
  font-size: 0.62rem;
  color: var(--text-muted);
  text-align: right;
}

/* ── MONTH TABS ── */
.month-tabs {
  display: flex;
  gap: 6px;
}

.month-tab {
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--border);
  color: var(--text-muted);
  background: var(--bg);
  transition: all 0.15s;
}

.month-tab.active {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
  color: #fff;
  border-color: var(--sidebar);
}

.month-tab:hover:not(.active) {
  border-color: var(--purple-border);
  color: var(--purple);
}

/* ── EMPTY STATE ── */
.empty-state-small {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-muted);
}

.empty-state-small p {
  font-size: 0.82rem;
  margin: 0;
}

/* ── ANIMATIONS ── */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── RESPONSIVE ── */
@media (max-width: 1200px) {
  .bottom-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .main {
    margin-left: 0;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .commission-banner {
    grid-template-columns: 1fr;
  }

  .commission-breakdown {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>
